import React, { useState, useEffect } from "react";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import KPICard from "./components/KPICard";
import OperationsChart from "./components/OperationsChart";
import axios from "axios";
import API_BASE_URL from "../../services/api";
import Banner from "./components/Banner";
import Prescription from "./components/Prescriptions";
import Patients from "./components/Patients";
import Orders from "./components/Orders";
import DueReportsDashboard from "./components/DueReportsDashboard";
import TodayActivity from "./components/TodayActivity";

const NovusOperationsHub = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [timeRange, setTimeRange] = useState("day");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(1);

  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [kpiData, setKpiData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchKPIData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/dashboard/kpi`);
      if (response.data.success) {
        setKpiData(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching KPI data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKPIData();

    if (autoRefresh) {
      const interval = setInterval(
        () => {
          fetchKPIData();
          setLastUpdated(new Date());
        },
        refreshInterval * 60 * 1000,
      );
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval]);

  const chartData = [
    {
      time: "00:00",
      patientVolume: 45,
      satisfactionScore: 4.2,
      efficiencyRate: 78,
    },
    {
      time: "04:00",
      patientVolume: 32,
      satisfactionScore: 4.5,
      efficiencyRate: 82,
    },
    {
      time: "08:00",
      patientVolume: 89,
      satisfactionScore: 4.1,
      efficiencyRate: 75,
    },
    {
      time: "12:00",
      patientVolume: 156,
      satisfactionScore: 4.3,
      efficiencyRate: 88,
    },
    {
      time: "16:00",
      patientVolume: 134,
      satisfactionScore: 4.6,
      efficiencyRate: 91,
    },
    {
      time: "20:00",
      patientVolume: 98,
      satisfactionScore: 4.8,
      efficiencyRate: 85,
    },
  ];

  const handleMenuToggle = () => setSidebarOpen(!sidebarOpen);
  const handleSidebarClose = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onMenuToggle={handleMenuToggle} isMenuOpen={sidebarOpen} />
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isCollapsed={sidebarCollapsed}
        onCollapseToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main
        className={`pt-16 transition-all duration-200 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}>
        <div className="p-4 sm:p-6">
          <Breadcrumb />

          {/* Page Header */}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">
              Novus Operations Hub
            </h1>
            <p className="text-sm text-gray-600">
              Real-time operational oversight and performance monitoring
            </p>
          </div>
          {/* Auto Refresh Controls */}
          <div className="flex items-center gap-4 mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={autoRefresh}
                onChange={() => setAutoRefresh(!autoRefresh)}
              />
              Auto Refresh (every {refreshInterval} min)
            </label>

            <button
              onClick={() => fetchKPIData()}
              className="text-sm px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700">
              Refresh
            </button>

            <span className="text-xs text-gray-500">
              Last Updated: {lastUpdated.toLocaleTimeString()}
            </span>
          </div>

          {/* ✅ Top Section: Equal height layout (responsive) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6 items-stretch">
            {/* LEFT SIDE: two cards split equally */}
            <div className="grid grid-rows-2 gap-4 h-full">
              <div className="bg-white rounded-lg border border-gray-200 h-full">
                <Orders />
              </div>

              <div className="bg-white rounded-lg border border-gray-200 h-full">
                <Prescription />
              </div>
            </div>

            {/* RIGHT SIDE: full height = left column height */}
            <div className="bg-white rounded-lg border border-gray-200 h-full flex flex-col overflow-hidden">
              <TodayActivity />
            </div>
          </div>

          {/* KPI Cards Grid */}
          {loading ? (
            <div className="text-center text-gray-500 py-6">
              Loading operational data...
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {kpiData?.map((kpi, index) => (
                <KPICard
                  key={index}
                  title={kpi?.title}
                  value={kpi?.value}
                  unit={kpi?.unit}
                  change={kpi?.change}
                  changeType={kpi?.changeType}
                  icon={kpi?.icon}
                  color={kpi?.color}
                />
              ))}
            </div>
          )}

          <div className="grid grid-cols-1  gap-4 mb-6">
            <div className="xl:col-span-1">
              <Patients />
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default NovusOperationsHub;
